import React, { useState } from "react";
import axios from "axios";
import { Speaker } from "lucide-react";
import { Link } from "react-router-dom";

const languages = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "bn", label: "Bengali" },
  { code: "ar", label: "Arabic" },
  { code: "he", label: "Hebrew" },
  { code: "hi", label: "Hindi" },
  { code: "ru", label: "Russian" },
];

const CorrectionForm = ({ token }) => {
  const [form, setForm] = useState({
    originalSentence: "",
    language: "en",
  });

  const [result, setResult] = useState({
    corrected: "",
  });

  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [savingFlashcard, setSavingFlashcard] = useState(false);

  const findLanguageLabel = (code) => {
    const lang = languages.find((l) => l.code === code);
    return lang ? lang.label : code;
  };

  const playWithBrowserTTS = (text, langCode) => {
    if (!window.speechSynthesis) {
      alert("Your browser does not support Text-to-Speech.");
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = langCode;
    utterance.rate = 1;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitCorrection = async () => {
    if (!form.originalSentence.trim()) {
      setMessage("Please enter the original sentence.");
      return;
    }

    setMessage("");
    setIsLoading(true);
    setResult({ corrected: "" });

    try {
      const response = await axios.post(
        "https://langbridge-backend.onrender.com/api/correction",
        {
          sentence: form.originalSentence,
          sourceLang: form.language,
          saveAsFlashcard: false,
        }
      );

      const { corrected } = response.data;
      setResult({ corrected });
    } catch (error) {
      console.error("Correction failed:", error);
      setMessage("Failed to get correction. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFlashcard = async () => {
    if (!token) {
      setShowLoginPrompt(true);
      return;
    }

    setSavingFlashcard(true);
    setMessage("");

    try {
      const response = await axios.post(
        "https://langbridge-backend.onrender.com/api/correction",
        {
          sentence: form.originalSentence,
          sourceLang: form.language,
          saveAsFlashcard: true,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setResult({ corrected: response.data.corrected });
      setMessage("Flashcard saved successfully!");
    } catch (error) {
      console.error("Save failed:", error);
      setMessage("Failed to save flashcard. Please try again.");
    } finally {
      setSavingFlashcard(false);
    }
  };

  return (
    <div className="bg-gray-50 rounded-xl p-6 w-full max-w-3xl mx-auto pt-6">
      <h2 className="text-2xl font-bold mb-6 text-center text-blue">
        Submit a Sentence Correction
      </h2>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1 text-purple">
          Language
        </label>
        <select
          name="language"
          value={form.language}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded px-3 py-2"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium mb-1 text-purple">
          Original Sentence
        </label>
        <textarea
          name="originalSentence"
          value={form.originalSentence}
          onChange={handleChange}
          rows={3}
          placeholder="Enter the sentence you want to correct..."
          className="w-full border border-gray-300 rounded px-3 py-2 resize-none"
        />
        <button
          type="button"
          onClick={() =>
            playWithBrowserTTS(form.originalSentence, form.language)
          }
          aria-label={`Play corrected sentence in ${findLanguageLabel(
            form.language
          )}`}
          title={`Play corrected sentence (${findLanguageLabel(
            form.language
          )})`}
          className="text-blue hover:text-purple mb-4"
        >
          <Speaker className="w-6 h-6 inline" />
        </button>
      </div>

      <div className="flex justify-center gap-4 mb-4">
        <button
          onClick={handleSubmitCorrection}
          className="bg-purple text-white py-2 px-6 rounded hover:bg-blue disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? "Correcting..." : "Submit Correction"}
        </button>
      </div>

      {message && (
        <p
          className={`text-center text-sm ${
            message.includes("Failed") ? "text-red-600" : "text-green-600"
          }`}
        >
          {message}
        </p>
      )}

      {result.corrected && (
        <div className="bg-gray rounded p-4 mt-6">
          <h3 className="text-lg font-semibold mb-2 text-blue">
            Corrected Sentence
          </h3>
          <textarea
            readOnly
            value={result.corrected || ""}
            className="w-full border border-gray-300 rounded px-3 py-2 resize-none mb-2"
            rows={3}
          />
          <button
            type="button"
            onClick={() => playWithBrowserTTS(result.corrected, form.language)}
            aria-label={`Play corrected sentence in ${findLanguageLabel(
              form.language
            )}`}
            title={`Play corrected sentence (${findLanguageLabel(
              form.language
            )})`}
            className="text-blue hover:text-purple mb-4"
          >
            <Speaker className="w-6 h-6 inline" />
          </button>

          <div className="mt-4">
            <button
              onClick={handleSaveFlashcard}
              disabled={savingFlashcard}
              className="bg-blue text-white py-2 px-4 rounded hover:bg-purple disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {savingFlashcard ? "Saving..." : "Save as Flashcard"}
            </button>
          </div>
        </div>
      )}

      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-2 text-purple">
              Login Required
            </h2>
            <p className="mb-4 text-sm text-gray-700">
              You must be logged in to save a flashcard.
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/login"
                className="bg-blue text-white px-4 py-2 rounded hover:bg-purple transition"
              >
                Go to Login
              </Link>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrectionForm;
